import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {DOCUMENT_TYPE, TABLE_COLUMNS_TYPES, WORKFLOW_PROCESS_STATUS} from 'utils/constants';
import SearchGroupInput from '../input/SearchGroupInput.component';
import {LinkedDateFilters} from '../input/CalendarInput.component';
import {NewCalendar} from 'components/common/calendar/Calendar.component';
import {DropdownSelect} from "components/common/dropdown/DropdownSelect.component";
import {DropdownSelectTypes, DropdownPosition} from "types/gaiaComponents/dropdownButton.types";
import {launchDarkly} from "utils/launchDarkly";
import moment from "moment";
import Checkbox from "components/common/checkbox.component";
import { CircleX } from "lucide-react";
import { ICON_SIZE } from "components/common/constants";
import { Button, ButtonType } from "components/common/Button.component";

const COLUMN_LEFT_RIGHT_MARGIN =   2 * 4;

// The search-row `td`s use 3px padding (see resizable_table.scss / my_history.scss), so
// the visible gap between two adjacent filters is 6px (left cell's right padding + right
// cell's left padding). The first column, however, sits flush against the table's left
// edge with only its own 3px padding, so when it is itself a filter its left margin looks
// tighter than the gaps between filters. Give that leading filter the full 6px on the left
// so its left margin matches the spacing between filters. Columns that are not filters
// (e.g. a leading checkbox) are left untouched: the first filter after them already sits a
// full gap away.
const SEARCH_ROW_CELL_PADDING = 4;
const LEADING_FILTER_LEFT_PADDING = 2 * SEARCH_ROW_CELL_PADDING;
// Extra left padding added to the leading cell beyond its normal padding. Text/default
// cells size their inner wrapper to the cell width, so shrink that wrapper by the same
// amount to keep the input from overflowing (and clipping) on the right.
const LEADING_FILTER_EXTRA_PADDING = LEADING_FILTER_LEFT_PADDING - SEARCH_ROW_CELL_PADDING;

// A date filter's selected value is the query it produced: one ISO day, or the two ends
// of a range joined by this separator (see dateSelect).
const DATE_RANGE_SEPARATOR = "->";
const ISO_DAY = "YYYY-MM-DD";

const parseDateFilterValue = (selectedValue) => {
    if (!selectedValue || selectedValue === -1)
        return null;

    const [from, to] = String(selectedValue).split(DATE_RANGE_SEPARATOR);
    const startDate = moment(from, ISO_DAY, true);
    if (!startDate.isValid())
        return null;

    const endDate = to ? moment(to, ISO_DAY, true) : startDate;
    return {
        startDate: startDate.toDate(),
        endDate: endDate.isValid() ? endDate.toDate() : startDate.toDate()
    };
};

export class TableSearchRow extends Component {
    constructor(props) {
        super(props);
        this.state = {
            companyDocumentTypes: [],
            processStates: [],
            companyId: props.companyId
        }
        this.dateSelect = this.dateSelect.bind(this);
        this.defaultSearch = this.defaultSearch.bind(this);
        this.removeDate = this.removeDate.bind(this);
        this.clearSearch = this.clearSearch.bind(this);
        this.clearAllFilters = this.clearAllFilters.bind(this);
        this.selectSearch = this.selectSearch.bind(this);
        this.getDocTypeTile = this.getDocTypeTile.bind(this);
        this.getStatusTitle = this.getStatusTitle.bind(this);
        this.getRoleTitle = this.getRoleTitle.bind(this);
        this.selectAllCheckboxEvent = this.selectAllCheckboxEvent.bind(this);
    }

    /*
     * we need to activate/deactivate the advanced filter at company context changes
     * */
    componentDidUpdate(prevProps) {
        if (prevProps.companyId !== this.props.companyId) {
            this.setState({
                companyId: this.props.companyId
            });
            this.loadFiltersData();
        }
    }

    removeDate(value, event) {
        event.stopPropagation();
        event.preventDefault();
        let newQuery = {};
        newQuery[value] = -1;
        this.props.onSearch(newQuery, value);
    }

    dateSelect(value, {date}) {

        let newQuery = {};
        let startDate = moment(date.startDate).format("YYYY-MM-DD");
        let endDate = moment(date.endDate).format("YYYY-MM-DD");
        //interval dates need special formatting

        if (startDate !== endDate) {
            newQuery[value] = startDate + "->" + endDate;
        } else {
            newQuery[value] = startDate;
        }
        this.props.onSearch(newQuery, value);
    }

    defaultSearch(value, event) {
        let newQuery = {};
        const regex_emoji = /[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F9B0}-\u{1F9B3}]+/gu;
        newQuery[value] = event.target.value !== "" ? event.target.value : -1;
        if (!regex_emoji.test(event.target.value))
            this.props.onSearch(newQuery, value);
    };

    selectSearch(property, value) {
        let newQuery = {};
        newQuery[property] = value;
        this.props.onSearch(newQuery, property);
    }

    clearSearch(value, event) {
        let newQuery = {};
        newQuery[value] = -1;
        this.props.onSearch(newQuery, value);
    }

    clearAllFilters(event) {
        this.props.onClearAllFilters();
    }

    getDocTypeTile(column) {
        if (column.selectedValue === -1 || column.selectedValue === "") {
            return this.props.translate("myHistory.all");
        }
        return this.props.translate("documentType." + DOCUMENT_TYPE.asString(parseInt(column.selectedValue)));
    }

    getStatusTitle(column) {
        if (column.selectedValue === -1 || column.selectedValue === "") {
            return this.props.translate("myHistory.all");
        }
        return this.props.translate("workflowProcessStatus." + WORKFLOW_PROCESS_STATUS.asString(parseInt(column.selectedValue)));
    }

    getRoleTitle(column) {
        if (column.selectedValue === -1 || column.selectedValue === "") {
            return this.props.translate("myHistory.all");
        }
        let roles = this.props.selectors[column.type];
        const columnSelected = parseInt(column.selectedValue);
        const matchingRole = roles.find(role => role.actorTypeCode.toString() === column.selectedValue.toString());
        return matchingRole ? matchingRole.actorTypeName : "";
    }

    getDeputyTitle(column) {
        if (column.selectedValue === -1 || column.selectedValue === "") {
            return this.props.translate("myHistory.all");
        }
        return this.props.translate(column.selectedValue.toString() === "true" ? "popUp.yes" : "popUp.no");
    }

    // Builds the Gaia DropdownSelect items for a filter column: a leading "All" option
    // followed by one option per selector entry. getValue/getText map each entry to its
    // query value and label; allValue is the query value used by the "All" option.
    buildFilterItems(column, options, getValue, getText, allValue) {
        const selectedValue = column.selectedValue;
        const isAllSelected = selectedValue === -1 || selectedValue === "" || selectedValue == null;
        const items = [{
            key: "all",
            text: this.props.translate("myHistory.all"),
            onClick: () => this.selectSearch(column.searchValue, allValue),
            selected: isAllSelected,
        }];
        (options || []).forEach((option) => {
            const value = getValue(option);
            items.push({
                key: String(value),
                text: getText(option),
                onClick: () => this.selectSearch(column.searchValue, value),
                selected: !isAllSelected && selectedValue != null && selectedValue.toString() === value.toString(),
            });
        });
        return items;
    }

    selectAllCheckboxEvent(event){
        event.stopPropagation();
        event.preventDefault();
        this.props.selectAll(event);
    }

    render() {
        const useNewCalendar = launchDarkly.userClient?.variation("fe-use-new-calendar", false) ?? false;

        let cells = this.props.columns.map((column, index) => {
            let columnWidth = column.width;
            // Only the very first column can sit flush against the table's left edge; later
            // filters already have a full gap from the preceding cell.
            const leadingFilterStyle = index === 0 ? {paddingLeft: LEADING_FILTER_LEFT_PADDING} : null;

            switch (column.type) {
                case TABLE_COLUMNS_TYPES.TEXT:
                case TABLE_COLUMNS_TYPES.AMOUNT:
                case TABLE_COLUMNS_TYPES.NUMBER: {
                    return (
                        <td key={index} style={{width: columnWidth, ...leadingFilterStyle}}
                            headers={column.searchValue + "-" + index}
                            className="no-hover"
                            id={column.searchValue + "_search"}>
                            <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN - (leadingFilterStyle ? LEADING_FILTER_EXTRA_PADDING : 0)}}>
                                <SearchGroupInput value={column.selectedValue}
                                                  ariaLabel={column.header?.label}
                                                  onInputChange={this.defaultSearch.bind(null, column.searchValue)}
                                                  onClearInput={this.clearSearch.bind(null, column.searchValue)}
                                                  translate={this.props.translate}
                                />
                            </div>
                        </td>);
                }
                case TABLE_COLUMNS_TYPES.CHECKBOX: {
                    return (
                        <td key={index} style={{textAlign: "center", width: columnWidth}}
                            headers={column.searchValue + "-" + index}
                            className="no-hover"
                            id={column.searchValue + "_search"}>
                            <div className={"checkbox w-100 h-100 d-flex align-items-center mx-0 ps-2 pe-3"} onClick={this.selectAllCheckboxEvent}>
                                <Checkbox
                                    inputId={"headerChk"}
                                    onChange={()=>{}}
                                    isChecked={this.props.allRowsSelected}
                                />
                            </div>
                        </td>);
                }
                case TABLE_COLUMNS_TYPES.SELECT: {
                    const items = this.buildFilterItems(
                        column,
                        this.props.selectors['documentType'],
                        (type) => type,
                        (type) => this.props.translate("documentType." + DOCUMENT_TYPE.asString(type)),
                        ""
                    );
                    return (
                        <td key={index} id={column.searchValue + "_search"} headers={column.searchValue + "-" + index}
                        className={"no-hover"} style={{width: columnWidth, ...leadingFilterStyle}}>
                            <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN}}>
                                <DropdownSelect id="documentTypeSelect"
                                                type={DropdownSelectTypes.VALUE}
                                                position={DropdownPosition.BOTTOM_LEFT}
                                                value={this.getDocTypeTile(column)}
                                                items={items}
                                                extraClasses="w-100"
                                                isEmpty={items.length === 0}/>
                            </div>
                        </td>
                    );
                }
                case TABLE_COLUMNS_TYPES.ROLE_SELECT: {
                    const items = this.buildFilterItems(
                        column,
                        this.props.selectors[column.type],
                        (type) => type.actorTypeCode,
                        (type) => type.actorTypeName,
                        ""
                    );
                    return (
                        <td key={index} id={column.searchValue + "_search"} headers={column.searchValue + "-" + index}
                        className={"no-hover"} style={{width: columnWidth, ...leadingFilterStyle}}>
                            <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN}}>
                                <DropdownSelect id="RoleSelect"
                                                type={DropdownSelectTypes.VALUE}
                                                position={DropdownPosition.BOTTOM_LEFT}
                                                value={this.getRoleTitle(column)}
                                                items={items}
                                                extraClasses="w-100"
                                                isEmpty={items.length === 0}/>
                            </div>
                        </td>);
                }
                case TABLE_COLUMNS_TYPES.DEPUTY_SELECT: {
                    const items = this.buildFilterItems(
                        column,
                        ["true", "false"],
                        (value) => value,
                        (value) => this.props.translate(value === "true" ? "popUp.yes" : "popUp.no"),
                        -1
                    );
                    return (
                        <td key={index} id={column.searchValue + "_search"} headers={column.searchValue + "-" + index}
                        className={"no-hover"} style={{width: columnWidth, ...leadingFilterStyle}}>
                            <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN}}>
                                <DropdownSelect id="DeputySelect"
                                                type={DropdownSelectTypes.VALUE}
                                                position={DropdownPosition.BOTTOM_LEFT}
                                                value={this.getDeputyTitle(column)}
                                                items={items}
                                                extraClasses="w-100"
                                                isEmpty={items.length === 0}/>
                            </div>
                        </td>);
                }
                case TABLE_COLUMNS_TYPES.STATUS: {
                    const items = this.buildFilterItems(
                        column,
                        this.props.selectors[column.type],
                        (type) => type.statusCode,
                        (type) => type.status,
                        -1
                    );
                    return (
                        <td key={index} id={column.searchValue + "_search"} headers={column.searchValue + "-" + index}
                        className={"no-hover"} style={{width: columnWidth, ...leadingFilterStyle}}>
                            <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN}}>
                                <DropdownSelect id="StatusSelect"
                                                type={DropdownSelectTypes.VALUE}
                                                position={DropdownPosition.BOTTOM_LEFT}
                                                value={this.getStatusTitle(column)}
                                                items={items}
                                                extraClasses="w-100"
                                                isEmpty={items.length === 0}/>
                            </div>
                        </td>);
                }
                case TABLE_COLUMNS_TYPES.DATE_FILTER: {
                    if (useNewCalendar) {
                        const selected = parseDateFilterValue(column.selectedValue);

                        return (
                            <td key={index} style={{width: columnWidth, ...leadingFilterStyle}}
                                headers={column.searchValue + "-" + index}
                                className="no-hover"
                                id={column.searchValue + "_search"}>
                                <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN - (leadingFilterStyle ? LEADING_FILTER_EXTRA_PADDING : 0)}}>
                                    <NewCalendar
                                        // The calendar reads its dates on mount only, so it is
                                        // remounted whenever the filter changes underneath it.
                                        key={String(column.selectedValue)}
                                        id={column.searchValue + "_datepicker"}
                                        translate={this.props.translate}
                                        // A Gaia input is a fixed 200px wide, which does not
                                        // fit a filter column. Let it take the cell instead.
                                        fieldClassName="w-100"
                                        {...(selected && {
                                            prefilled: true,
                                            initialStartDate: selected.startDate,
                                            initialEndDate: selected.endDate
                                        })}
                                        onChange={(range) => this.dateSelect(column.searchValue, {date: range})}
                                        onClear={() => this.clearSearch(column.searchValue)}
                                    />
                                </div>
                            </td>
                        );
                    }

                    return (
                        <LinkedDateFilters
                            setid={column.searchValue + "_search"}
                            headers={column.searchValue + "-" + index}
                            key={index} column={column}
                            width={columnWidth - COLUMN_LEFT_RIGHT_MARGIN}
                            leadingPaddingLeft={leadingFilterStyle ? LEADING_FILTER_LEFT_PADDING : undefined}
                            removeDate={this.removeDate}
                            translate={this.props.translate}
                            dateSelect={this.dateSelect}
                        />
                    );
                }
                case TABLE_COLUMNS_TYPES.CLEAR_ALL_FILTERS: {
                    const isDisabled = Object.keys(this.props.filters.columns).length === 0;

                    return (
                        <td key="clear_all_search" className="text-center p-0 no-hover"
                            style={{width: "40px"}} id={"clear_all_search_search"}
                            headers={"clear_all_search-" + index}>
                             <Button type={ButtonType.GHOST}
                                     iconOnly={true}
                                     icon={<CircleX size={ICON_SIZE.MEDIUM}/>}
                                     extraClassName="clear-all-filters-icon"
                                     disabled={isDisabled}
                                     onClick={this.clearAllFilters}
                                     ariaLabel={this.props.translate("myHistory.clearAllFilters")}
                                     tooltip={{id: "clear-all-filters-tooltip", content: () => this.props.translate("myHistory.clearAllFilters")}}/>
                        </td>);
                }
                case TABLE_COLUMNS_TYPES.NONE:
                case TABLE_COLUMNS_TYPES.TASK_STATUS:
                    return (
                        <td key={index} id={column.searchValue + "_search"} headers={column.searchValue + "-" + index}
                        className={"no-hover"}>
                            <span/></td>);
                default: {
                    return (
                        <td key={index} style={{width: columnWidth, ...leadingFilterStyle}} id={column.searchValue + "_search"}
                        className={"no-hover"}>
                            <div style={{width: columnWidth - COLUMN_LEFT_RIGHT_MARGIN - (leadingFilterStyle ? LEADING_FILTER_EXTRA_PADDING : 0)}}>
                                <SearchGroupInput value={column.selectedValue}
                                                  ariaLabel={column.header?.label}
                                                  onInputChange={this.defaultSearch.bind(null, column.searchValue)}
                                                  onClearInput={this.clearSearch.bind(null, column.searchValue)}
                                                  translate={this.props.translate}
                                />
                            </div>
                        </td>);
                }
            }
        });
        return (
            <tr>
                {cells}
            </tr>
        );
    }
}

TableSearchRow.propTypes = {
    translate: PropTypes.func, //translation function
    filters: PropTypes.object, //discribes the filters from the store
    columns: PropTypes.array,//array of columns to display
    onSearch: PropTypes.func, //translation function
}

export default TableSearchRow;